// @safetyhub/report-composer — 평가방식(findingRiskMethod) 변경 재resolve 병합
//
// 작성 화면(web)·admin 캔버스에서 위험도 표시 **방식**(카탈로그 파라미터 지정이 참조하는 변수.
// 예: assessmentMethod='3x3'/'5x5'/'THREE_LEVEL')을 바꾸면, 그 방식을
// findingRiskMethod로 서버 prefill을 재resolve한 결과(fresh)를 현재 작성값(current)에 **선별 병합**한다.
// 트리거 판정 자체는 `catalogParamTrigger.ts`가 소유한다(서버 resolveFindingRiskMethod의 거울).
//
// 확정 의미론(결정 2026-07-20-risk-estimation-snapshot-user-edit-priority + P4e):
//   - 위험도 파생 필드(riskEstimation/riskEstimationAfter)·`<dsKey>__seed`·기준표(config/카탈로그
//     채널)만 fresh로 교체.
//   - 옛 방식에서의 위험도 편집은 **폐기**(새 어휘에서 무의미 + seed 갱신으로 P4c 편집 판정 재정렬).
//   - 감소대책 등 **비위험도 편집 필드는 보존**.
//   - 방식 구동 변수 자신(사용자의 방식 선택)과 그 외 사용자 입력 키는 **무차별 덮기 금지**(보존).
//
// 서버 overrideServerDerivedRisk(제출 경로)와 화이트리스트(RISK_DERIVED_PRIMARY_KEYS)는 동일하나,
// 제출 경로는 seed 대조로 "편집=보존/미편집=서버값"을 하는 반면, 여기(방식 변경 시점)는 방식 자체가
// 바뀌므로 위험도는 **무조건 fresh 채택**(옛 방식 편집 폐기)한다.
//
// 편집 모드(web `?editReportId=`)에도 같은 규약이 적용된다(결정 2026-09-14 RA 전환 문서 「편집 모드 평가 방식
// 변경」 개정 — 07-20의 「같은 방식 재prefill은 편집을 덮지 않는다」와 양립). 다만 행 **집합**의 기준이 다르다:
// 편집은 저장 content가 행의 정본이라 `options.rowBasis:'current'`로 행 구성(순서·삭제·추가)을 보존하고
// 위험도만 rowId 매칭으로 교체한다. 서버 update() 경로는 박제(`__catalogParams`)·방식 채널을 같은 축으로
// 갱신한다(`ReportComposerService.restampRiskMethodOnEdit`).

import {
  ROWS_SEED_SUFFIX,
  REGISTERED_FORM_WIDGETS_META,
  RISK_METHOD_CHANNEL_KEYS,
  type Binding,
  type ComposerNode,
  type ReportTemplate,
  type ComposerWidget,
  type InputWidget,
} from '@safetyhub/shared';
import { collectCatalogParamVariableKeys } from './catalogParamTrigger';
import { snapshotKey } from './resolver';

/** 방식 재resolve가 교체 대상으로 삼는 위험도 파생 primary 필드(서버 overrideServerDerivedRisk 화이트리스트와 동일). */
const RISK_DERIVED_PRIMARY_KEYS = new Set(['riskEstimation', 'riskEstimationAfter']);

/**
 * 방식 변경 시 fresh로 교체할 config/카탈로그 채널(기준표) — 사용자 입력 키와 구분하는 근거.
 * 이 카탈로그를 소스로 하는 바인딩(기준표 표시 위젯 등)만 교체하고, 나머지 사용자 입력은 보존한다.
 */
const RISK_CONFIG_CATALOGS = new Set([
  'risk-assessment-config',
  'risk-assessment-configs',
]);

/**
 * 서버 resolvePrefill(3-f)이 위험도 조립(branch+select)용으로 주입하는 top-level 키 —
 * config 통짜 + 방식 파생 flat 키(방식 조건 branch용) + 기준표 criteria rows(방식별 select optionsFrom).
 * 방식이 바뀌면 이 키들도 fresh 재resolve본(새 방식 기준)으로 교체해야 옛 기준표 select 옵션·
 * 방식 조건이 남지 않는다(사용자 입력 아님 — config 파생이라 항상 fresh 채택).
 * 목록은 shared `RISK_METHOD_CHANNEL_KEYS`가 정본 — 서버 `restampRiskMethodOnEdit`의 「먼저 지우는」
 * 집합과 같은 것이라 손으로 갈라지지 않게 그대로 쓴다(통짜 키는 RISK_CONFIG_CATALOGS와 겹치지만
 * 교체 규칙이 같아 무해).
 */
const RISK_METHOD_INJECTED_KEYS: readonly string[] = RISK_METHOD_CHANNEL_KEYS;

/**
 * 위험도 config 채널의 **점 표기 파생 키**인가 — `risk-assessment-config.matrixType`처럼
 * 서버 prefill이 entry를 평탄화해 함께 싣는 키들이다(`.methodType`·`.matrixType`·
 * `.riskLevelCriteria`·`.likelihoodCriteria`·`.severityCriteria`·`.levelCriteria`).
 *
 * ⚠️ **이 키들도 사용자 입력이 아니라 config 파생이라 방식이 바뀌면 항상 fresh여야 한다.**
 * 종전에는 교체 목록에 객체 키(`risk-assessment-config`)만 있고 그 점 표기 자식들이 빠져 있어,
 * 방식을 바꿔도 **옛 방식 값이 그대로 남았다**. 본문 `BranchNode.conditionBindings`가 바로 이
 * 키들을 읽어 분기(예: 3×3/5×5 행렬 선택)를 정하므로, 결과는 "기준표는 새 방식인데 표는 옛
 * 방식"이었다(2026-08-07 실측: 5×5로 바꾼 캔버스에 3×3 행렬 + 5×5 판정이 함께 나옴).
 * 세 경로(web 작성·admin 캔버스·SimulationPage)가 이 병합을 공유하므로 배포 경로도 같았다 —
 * 방식을 바꾸고 제출하면 그 stale 키가 content에 박힌다.
 *
 * 접두만 보고 걷는 이유: 서버가 싣는 파생 키 목록을 여기 손으로 복제하면 서버가 필드를 늘릴 때
 * 조용히 갈라진다(그 실패는 "선언은 맞는데 값만 옛것"이라 알아채기 어렵다). 접두 규칙이면
 * 서버가 무엇을 더 싣든 따라온다. 과하게 걷어도 안전하다 — 이 접두의 값은 전부 config 파생이라
 * 사용자 입력이 섞이지 않는다.
 */
function isRiskConfigDerivedKey(key: string): boolean {
  for (const catalog of RISK_CONFIG_CATALOGS) {
    if (key.startsWith(`${catalog}.`)) return true;
  }
  return false;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * 본문(body) ComposerNode 트리의 모든 Binding 방문 — resolver `walkComposeNode`와 **같은 순회**다
 * (node.binding / branch conditionBindings / 표 셀 binding·compute 입력 / 중첩 셀 content).
 * compose가 박제하는 바인딩의 전집합이 곧 이 순회이므로, 교체 키 도출도 같은 걸음이어야
 * "compose는 박는데 병합은 모르는" 키가 생기지 않는다.
 */
function walkBodyBindings(nodes: ComposerNode[], visit: (b: Binding) => void): void {
  for (const node of nodes) {
    if (node.binding) visit(node.binding);
    switch (node.kind) {
      case 'group':
      case 'stack':
        walkBodyBindings(node.children, visit);
        break;
      case 'repeat':
        walkBodyBindings([node.template], visit);
        break;
      case 'branch':
        for (const cb of node.conditionBindings ?? []) visit(cb.binding);
        for (const slot of node.branches) walkBodyBindings(slot.children, visit);
        break;
      case 'table':
        for (const cell of node.cells) {
          if (cell.binding) visit(cell.binding);
          if (cell.compute) {
            for (const inp of cell.compute.inputs) {
              if (inp.binding) visit(inp.binding);
            }
          }
          if (cell.content) walkBodyBindings(cell.content, visit);
        }
        break;
      default:
        break;
    }
  }
}

/**
 * 편집형 조립 데이터소스(kind:'assembled')별 위험도 파생 필드 목록.
 * def.fields 중 from이 `primary.riskEstimation`/`primary.riskEstimationAfter`인 필드의 출력 키
 * (서버 overrideServerDerivedRisk의 primaryFields 도출과 동일 규칙). 위험도 필드가 없는 ds는 제외.
 */
export function riskDerivedFieldsByDsKey(
  template: ReportTemplate,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const ds of template.dataSources ?? []) {
    if (ds.kind !== 'assembled') continue;
    const riskFields = (ds.fields ?? [])
      .filter((f) => {
        if (typeof f.from !== 'string' || !f.from.startsWith('primary.')) return false;
        return RISK_DERIVED_PRIMARY_KEYS.has(f.from.slice('primary.'.length));
      })
      .map((f) => f.field);
    if (riskFields.length > 0) out.set(ds.key, riskFields);
  }
  return out;
}

/** ComposerWidget 트리를 재귀 순회하며 input 위젯만 방문(group/modalTrigger/repeat/branch children, formTable 고정 셀 위젯). */
function walkInputWidgets(
  nodes: ComposerWidget[],
  visit: (w: InputWidget) => void,
): void {
  for (const w of nodes) {
    if (w.kind === 'input') {
      visit(w);
    } else if (w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat') {
      walkInputWidgets(w.children, visit);
    } else if (w.kind === 'branch') {
      // branch: 투명 컨테이너 — 전 슬롯 children 하강(group 동형).
      for (const slot of w.branches) walkInputWidgets(slot.children, visit);
    } else if (w.kind === 'formTable') {
      for (const row of w.rows) {
        if (row.type !== 'fixed') continue;
        for (const cell of row.cells) {
          if (cell.widget) visit(cell.widget);
        }
      }
    }
  }
}

/**
 * 방식 변경 시 fresh로 교체할 **기준표(config/카탈로그) 바인딩 키** 집합.
 *
 * 소스 catalogKey가 위험도 config 카탈로그(RISK_CONFIG_CATALOGS)인 바인딩만 수집한다:
 *  - 변수(optionsFrom/prefillFrom)
 *  - 트리 input 위젯의 명시 prefillFrom, registered 위젯의 REGISTERED_FORM_WIDGETS_META.defaultPrefillFrom
 *  - dataDefs / writeForm의 prefillFrom
 *  - 본문 body 바인딩(branch conditionBindings·표 셀 binding 등, walkBodyBindings) —
 *    compose 박제 철자(snapshotKey)와 bindingKey **둘 다** 편입(접두 sweep이 못 덮는 철자 보강)
 *
 * **방식을 정하는 변수 자신의 키는 제외** — 그 값은 사용자의 방식 선택이라 재resolve로 덮으면 선택이
 * 무효화된다(무차별 덮기 금지 규약). 제외 대상은 **카탈로그 파라미터 지정이 참조하는 변수**다
 * (`collectCatalogParamVariableKeys`) — 저작자가 참조로 꽂은 select(예: 평가기준 세트)가 fresh
 * 값으로 덮이면 사용자가 고른 방식이 되돌아간다.
 */
export function collectMethodConfigKeys(template: ReportTemplate): Set<string> {
  const keys = new Set<string>();
  const driverKeys = collectCatalogParamVariableKeys(template);
  const isRiskCatalog = (c?: string): boolean => !!c && RISK_CONFIG_CATALOGS.has(c);

  for (const v of template.variables ?? []) {
    if (driverKeys.has(v.key)) continue;
    if (isRiskCatalog(v.optionsFrom?.catalogKey) || isRiskCatalog(v.prefillFrom?.catalogKey)) {
      keys.add(v.key);
    }
  }

  walkInputWidgets(template.composerWidgetTree ?? [], (w) => {
    if (driverKeys.has(w.bindingKey)) return;
    if (isRiskCatalog(w.prefillFrom?.catalogKey)) {
      keys.add(w.bindingKey);
      return;
    }
    if (w.widget === 'registered' && w.widgetKey) {
      const meta = REGISTERED_FORM_WIDGETS_META.find((m) => m.key === w.widgetKey);
      if (isRiskCatalog(meta?.defaultPrefillFrom?.catalogKey)) keys.add(w.bindingKey);
    }
  });

  for (const def of template.dataDefs ?? []) {
    if (isRiskCatalog(def.prefillFrom?.catalogKey)) keys.add(def.key);
  }

  // 본문(body) 바인딩 — branch conditionBindings·표 셀 binding 등이 위험도 config 카탈로그를
  // 소스로 갖는 경우. compose는 이 값을 **snapshotKey(binding)**(select가 단순 경로면 select —
  // 예: 점 없는 `matrixType`) 철자로 content에 박제하므로, 접두(`risk-assessment-config.`)
  // sweep만으로는 그 철자를 못 갱신하고, 저작자가 bindingKey를 관례와 다르게 지으면 통째
  // 빗나간다. 템플릿 선언에서 bindingKey와 snapshotKey **둘 다** 교체 집합에 편입해
  // 저장/읽기 어느 철자로 실려도 fresh로 덮이게 한다(방식 구동 변수 키는 종전대로 보존).
  walkBodyBindings(template.body ?? [], (b) => {
    if (!isRiskCatalog(b.catalogKey)) return;
    if (!driverKeys.has(b.bindingKey)) keys.add(b.bindingKey);
    const snap = snapshotKey(b);
    if (!driverKeys.has(snap)) keys.add(snap);
  });

  // 위험도 조립(branch+select) 주입 키 — 방식 구동 변수가 아니므로 driverKeys와 무관하게 항상 포함.
  for (const k of RISK_METHOD_INJECTED_KEYS) keys.add(k);

  return keys;
}

/**
 * 위험도 파생 필드만 fresh로 교체하고 비위험도 필드는 current 편집을 보존해 행 배열을 병합한다.
 *
 * 행 매칭: fresh 행의 rowId 우선(current를 rowId로 인덱스), rowId 부재 시 index 대응.
 * 행 집합은 **fresh 기준**(순서/멤버십) — fresh에 없는 행은 폐기, fresh에만 있는 신규 행은 fresh 전체 채택.
 * 매칭된 기존 행은 fresh에서 시작(fresh 위험도·구조) + current의 비위험도 필드(rowId 제외)를 덮어 승계.
 */
function mergeRiskRows(
  curRows: unknown[],
  freshRows: unknown[],
  riskFields: string[],
): unknown[] {
  const riskFieldSet = new Set(riskFields);
  const curByRowId = new Map<string, Record<string, unknown>>();
  for (const r of curRows) {
    if (isPlainObject(r) && typeof r.rowId === 'string' && r.rowId !== '') {
      curByRowId.set(r.rowId, r);
    }
  }
  return freshRows.map((fr, i) => {
    if (!isPlainObject(fr)) return fr;
    const rowId = typeof fr.rowId === 'string' && fr.rowId !== '' ? fr.rowId : undefined;
    let cur: Record<string, unknown> | undefined;
    if (rowId !== undefined) {
      cur = curByRowId.get(rowId);
    } else {
      const cand = curRows[i];
      if (isPlainObject(cand)) cur = cand;
    }
    if (!cur) return fr; // 방식 재조립으로 생긴 신규 행 — fresh 전체 채택(fresh 위험도 포함)
    const merged: Record<string, unknown> = { ...fr };
    for (const k of Object.keys(cur)) {
      if (k === 'rowId') continue; // rowId는 fresh 유지(매칭 시 동일 값)
      if (riskFieldSet.has(k)) continue; // 위험도 파생은 fresh 유지(옛 방식 편집 폐기)
      merged[k] = cur[k]; // 비위험도 편집 보존(감소대책 등)
    }
    return merged;
  });
}

/**
 * **현재 행 기준** 변형(편집 모드 — 결정 2026-09-14 「편집 모드 평가 방식 변경」 개정) — 위험도 파생
 * 필드만 fresh에서 가져오고 행 집합은 **current 그대로**(순서·삭제·추가 보존).
 *
 * 편집 모드는 저장된 content가 행의 정본이다: 작성자가 지운 행은 점검 재조립(fresh)에 여전히 있고,
 * 직접 추가한 행은 fresh에 없다. fresh 기준(mergeRiskRows)으로 병합하면 지운 행이 되살아나고
 * 추가한 행이 사라진다 — 방식 하나 바꿨는데 문서의 행 구성이 바뀌는 것은 편집자가 기대한 일이 아니다.
 * (분할 문서·다중 점검 합본도 같은 축이다 — fresh는 점검 전체를 재조립하므로 current 밖 행이 늘 있다.)
 *
 * 행 매칭: current 행의 rowId 우선(fresh를 rowId로 인덱스), rowId 부재 시 index 대응.
 *  - 매칭된 행: current에서 시작 + 위험도 파생 필드만 fresh 값으로 교체(옛 방식 편집 폐기).
 *  - 매칭 없는 행(작성자가 추가한 행 등): 위험도 파생 필드를 **비운다**(새 방식에서 미평가 — 옛 어휘의
 *    값을 남기면 새 기준표와 어긋난 채 저장된다). 비위험도 필드는 그대로.
 *  - fresh에만 있는 행: 편입하지 않는다.
 */
function mergeRiskRowsOnCurrent(
  curRows: unknown[],
  freshRows: unknown[],
  riskFields: string[],
): unknown[] {
  const freshByRowId = new Map<string, Record<string, unknown>>();
  for (const r of freshRows) {
    if (isPlainObject(r) && typeof r.rowId === 'string' && r.rowId !== '') {
      freshByRowId.set(r.rowId, r);
    }
  }
  return curRows.map((cr, i) => {
    if (!isPlainObject(cr)) return cr;
    const rowId = typeof cr.rowId === 'string' && cr.rowId !== '' ? cr.rowId : undefined;
    let fr: Record<string, unknown> | undefined;
    if (rowId !== undefined) {
      fr = freshByRowId.get(rowId);
    } else {
      const cand = freshRows[i];
      if (isPlainObject(cand)) fr = cand;
    }
    const merged: Record<string, unknown> = { ...cr };
    for (const k of riskFields) {
      if (fr && fr[k] !== undefined) merged[k] = fr[k]; // 새 방식 재산출 채택
      else delete merged[k]; // 새 방식에서 미평가 — 옛 어휘 값 폐기
    }
    return merged;
  });
}

/** `mergeMethodReresolve` 옵션 — 행 집합의 기준. */
export interface MergeMethodReresolveOptions {
  /**
   * 위험도 파생 편집형 조립 ds의 **행 집합 기준**.
   *  - `'fresh'`(기본, 종전): 점검 재조립(fresh) 기준 — 신규 작성·admin 캔버스. 삭제/신규 행은 fresh를 따른다.
   *  - `'current'`: 현재 작성값 기준 — 편집 모드(content가 행의 정본). 행 구성은 그대로 두고 위험도만
   *    rowId 매칭으로 교체, 미매칭 행은 위험도를 비운다(`mergeRiskRowsOnCurrent`).
   * 행 집합 외의 교체 규칙(`__seed`·기준표·config 채널·주입 키)은 두 기준이 같다.
   */
  rowBasis?: 'fresh' | 'current';
}

/**
 * 방식 재resolve 결과(fresh)를 현재 작성값(currentValues)에 선별 병합한 **새 values 객체**를 반환한다.
 *
 * 교체 대상(그 외 키는 현행 보존):
 *  1. 위험도 파생 편집형 조립 ds: 행 배열을 위험도 필드만 fresh로 교체 — 비위험도 편집 보존.
 *     행 집합은 `options.rowBasis`가 정한다(기본 'fresh' = mergeRiskRows / 'current' = mergeRiskRowsOnCurrent).
 *  2. `<dsKey>__seed`: fresh로 통째 교체(P4c 편집 판정 기준 재정렬).
 *  3. 기준표(config/카탈로그) 바인딩 키(collectMethodConfigKeys) + 위험도 config 채널 키(RISK_CONFIG_CATALOGS):
 *     fresh에 존재하면 교체. 방식 구동 변수·기타 사용자 입력 키는 제외(보존).
 */
export function mergeMethodReresolve(
  currentValues: Record<string, unknown>,
  fresh: Record<string, unknown>,
  template: ReportTemplate,
  options?: MergeMethodReresolveOptions,
): Record<string, unknown> {
  const next = { ...currentValues };
  const riskFieldsByKey = riskDerivedFieldsByDsKey(template);
  const rowBasis = options?.rowBasis ?? 'fresh';

  for (const [dsKey, riskFields] of riskFieldsByKey) {
    const freshRows = fresh[dsKey];
    if (Array.isArray(freshRows)) {
      const curRows = Array.isArray(currentValues[dsKey])
        ? (currentValues[dsKey] as unknown[])
        : [];
      next[dsKey] =
        rowBasis === 'current'
          ? mergeRiskRowsOnCurrent(curRows, freshRows, riskFields)
          : mergeRiskRows(curRows, freshRows, riskFields);
    }
    const seedKey = `${dsKey}${ROWS_SEED_SUFFIX}`;
    if (fresh[seedKey] !== undefined) next[seedKey] = fresh[seedKey];
  }

  for (const key of collectMethodConfigKeys(template)) {
    if (riskFieldsByKey.has(key)) continue;
    if (key.endsWith(ROWS_SEED_SUFFIX)) continue;
    if (fresh[key] !== undefined) next[key] = fresh[key];
  }

  // 위험도 config 채널 키(기준표 위젯이 resolvedRows/values로 읽는 단수/목록 entry) — 사용자 입력이 아닌
  // 서버 config 값이라 fresh에 있으면 항상 교체(RiskEstimationWidget의 라이브 기준 반영).
  for (const key of RISK_CONFIG_CATALOGS) {
    if (fresh[key] !== undefined) next[key] = fresh[key];
  }
  // 그 채널의 **점 표기 파생 키**도 같은 이유로 항상 교체(isRiskConfigDerivedKey 참조) —
  // 분기 조건(conditionBindings)이 읽는 키가 여기 있어, 빠뜨리면 표만 옛 방식으로 남는다.
  for (const key of Object.keys(fresh)) {
    if (isRiskConfigDerivedKey(key)) next[key] = fresh[key];
  }

  return next;
}

/**
 * mergeMethodReresolve가 fresh로 교체하는 키 집합(admin 캔버스 edits 계층 조정용).
 * admin은 default→prefill→edits 계층이라, 방식 변경 시 병합 결과를 edits에 실어 prefill을 덮어야
 * 옛 방식 위험도 편집이 사라지고 fresh가 보인다 — 이 키들만 edits에 반영한다.
 */
export function collectMethodReresolveTouchedKeys(
  fresh: Record<string, unknown>,
  template: ReportTemplate,
): Set<string> {
  const keys = new Set<string>();
  const riskFieldsByKey = riskDerivedFieldsByDsKey(template);
  for (const dsKey of riskFieldsByKey.keys()) {
    if (Array.isArray(fresh[dsKey])) keys.add(dsKey);
    const seedKey = `${dsKey}${ROWS_SEED_SUFFIX}`;
    if (fresh[seedKey] !== undefined) keys.add(seedKey);
  }
  for (const key of collectMethodConfigKeys(template)) {
    if (riskFieldsByKey.has(key)) continue;
    if (key.endsWith(ROWS_SEED_SUFFIX)) continue;
    if (fresh[key] !== undefined) keys.add(key);
  }
  for (const key of RISK_CONFIG_CATALOGS) {
    if (fresh[key] !== undefined) keys.add(key);
  }
  // 점 표기 파생 키 — mergeMethodReresolve와 **같은 규칙**이어야 한다. admin 캔버스는 이 집합만
  // edits에 실어 prefill을 덮으므로, 여기서 빠지면 병합은 맞는데 화면만 옛 값이 남는다.
  for (const key of Object.keys(fresh)) {
    if (isRiskConfigDerivedKey(key)) keys.add(key);
  }
  return keys;
}
